import type { PlanInfo } from '../types';
import { type ISubscription, PricingModalOrigin, TbillReason } from '@wegic/price-ui';
import { getAllUserProjects } from '@/helpers/project';
import { updateProjectSettings } from '@/services/project';
import { useProjectStore } from '@/stores/project';
import { useUserStore } from '@/stores/user';

/**
 * 根据用户订阅信息判断用户的权益类型
 */
export function getUserPlanInfo(userSubscription: ISubscription | null): PlanInfo {
  if (!userSubscription || !userSubscription.exist) {
    return {
      type: 'free',
      label: 'Free',
    };
  }

  const currentProduct = userSubscription.currentProduct;
  if (!currentProduct) {
    return {
      type: 'free',
      label: 'Free',
    };
  }

  const productName = currentProduct.name || '';
  const lowerProductName = productName.toLowerCase();

  if (lowerProductName.includes('pro') || lowerProductName.includes('premium')) {
    return { type: 'premium', label: 'Premium' };
  }
  if (lowerProductName.includes('basic') || lowerProductName.includes('starter')) {
    return { type: 'starter', label: 'Starter' };
  }

  return {
    type: 'free',
    label: 'Free',
  };
}

/**
 * 获取打开计划弹窗的类型
 * @param origin 打开计划弹窗的来源
 * @returns 打开计划弹窗的类型
 */
export function getPricingModalType(origin?: PricingModalOrigin): string {
  if ([PricingModalOrigin.PublishLimitation, PricingModalOrigin.Publish].includes(origin)) {
    return PricingModalOrigin.PublishLimitation;
  }

  if (origin === PricingModalOrigin.CustomDomain) {
    return PricingModalOrigin.CustomDomain;
  }

  return 'standard';
}

/** 获取账单原因 */
export function getBillReason() {
  const { userSubscription } = useUserStore.getState();
  return userSubscription?.exist ? TbillReason.UPGRADE : TbillReason.CREATE;
}

/** 权益在指定时间内到期 */
export function useSubscriptionEnd(userSubscription: ISubscription | undefined, interval: number) {
  return !!userSubscription?.endDate && ((userSubscription.endDate - userSubscription.nowDate) < interval);
}

/** 权益到期且未续订 */
export function useSubscriptionEndAndCancel(userSubscription: ISubscription | undefined, interval: number) {
  return useSubscriptionEnd(userSubscription, interval) && !!userSubscription?.cancel;
}

/**
 * 批量更新所有项目的移除徽标设置
 */
export async function updateAllProjectsRemoveBrandSwitch(removeBrandSwitch: boolean): Promise<void> {
  const projects = await getAllUserProjects();

  await Promise.allSettled(
    projects.map(p => updateProjectSettings(p.id, { REMOVE_BRAND_SWITCH: removeBrandSwitch })),
  );

  // 同步更新当前项目的本地 Store
  const { currentProjectSettings, updateCurrentProjectSettings } = useProjectStore.getState();
  if (currentProjectSettings) {
    updateCurrentProjectSettings({ REMOVE_BRAND_SWITCH: removeBrandSwitch });
  }
}
